export type { AdmissionType, Track, Department, CollegeWeights, College } from "./sharedTypes";
import type { College, CollegeWeights, Department, Gender, Scores, StepAlert, StepAlertLine, Track } from "./sharedTypes";

// College weight formulas from Kuwait University admission rules (2025/2026)
export const collegeWeights: Record<string, CollegeWeights> = {
    medicine: {
        highSchoolWeight: 75,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 10,
        englishWeight: 15,
        minHighSchool: 85,
        minEnglish: null,
        admissionType: "equivalent",
    },
    dentistry: {
        highSchoolWeight: 75,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 10,
        englishWeight: 15,
        minHighSchool: 85,
        minEnglish: null,
        admissionType: "equivalent",
    },
    pharmacy: {
        highSchoolWeight: 70,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 15,
        englishWeight: 15,
        minHighSchool: 80,
        minEnglish: null,
        admissionType: "equivalent",
    },
    public_health: {
        highSchoolWeight: 70,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 15,
        englishWeight: 15,
        minHighSchool: 80,
        minEnglish: null,
        admissionType: "equivalent",
    },
    allied_medical: {
        highSchoolWeight: 70,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 10,
        englishWeight: 20,
        minHighSchool: 80,
        minEnglish: null,
        admissionType: "equivalent",
    },
    engineering: {
        highSchoolWeight: 65,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 20,
        englishWeight: 15,
        minHighSchool: 80,
        minEnglish: null,
        admissionType: "equivalent",
    },
    architecture: {
        highSchoolWeight: 70,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 15,
        englishWeight: 15,
        minHighSchool: null,
        minEnglish: null,
        admissionType: "equivalent",
    },
    life_sciences: {
        highSchoolWeight: 70,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 15,
        englishWeight: 15,
        minHighSchool: null,
        minEnglish: null,
        additionalConditions: "personal_interview_communication_disorders",
        admissionType: "equivalent",
    },
    admin_sciences: {
        highSchoolWeight: 70,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 15,
        englishWeight: 15,
        minHighSchool: null,
        minEnglish: 40,
        additionalConditions: "english_40_percent",
        admissionType: "equivalent",
    },
    admin_sciences_literary: {
        highSchoolWeight: 70,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 15,
        englishWeight: 15,
        minHighSchool: null,
        minEnglish: 40,
        additionalConditions: "english_40_percent",
        admissionType: "equivalent",
    },
    education_scientific: {
        highSchoolWeight: 80,
        arabicWeight: 5,
        chemistryWeight: 0,
        mathWeight: 7.5,
        englishWeight: 7.5,
        minHighSchool: null,
        minEnglish: null,
        additionalConditions: "personal_interview",
        admissionType: "equivalent",
    },
    education_literary: {
        highSchoolWeight: 80,
        arabicWeight: 10,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 10,
        minHighSchool: null,
        minEnglish: null,
        additionalConditions: "personal_interview",
        admissionType: "equivalent",
    },
    arts: {
        highSchoolWeight: 85,
        arabicWeight: 15,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: null,
        minEnglish: null,
        additionalConditions: "french_50_percent",
        admissionType: "equivalent",
    },
    arts_english: {
        highSchoolWeight: 85,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 15,
        minHighSchool: null,
        minEnglish: null,
        admissionType: "equivalent",
    },
    sharia: {
        highSchoolWeight: 85,
        arabicWeight: 15,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: null,
        minEnglish: null,
        admissionType: "equivalent",
    },
    social_sciences: {
        highSchoolWeight: 90,
        arabicWeight: 10,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: null,
        minEnglish: null,
        additionalConditions: "personal_interview_social_service",
        admissionType: "equivalent",
    },
    science: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: null,
        minEnglish: null,
        admissionType: "high_school",
    },
    law: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: null,
        minEnglish: null,
        admissionType: "high_school",
    },
};

/* All departments with min accepted equivalent rates for 2026/2027, transcribed
 * from the two official KU PDFs (equalvant_rate/kuwait university/2027/):
 *   - scientific-kuwait-university-2027-min_equlvant_rate.pdf (علمي)
 *   - lectural-kuwait-unversity-2027-min-equalvant-rate.pdf (أدبي)
 * Science & Law pages publish نسبة الثانوية (HS %); all other colleges publish
 * المعدل المكافئ للقبول — matching each entry's `type` below. */
export const departments: Department[] = [
    // Medicine
    { code: "0511", collegeKey: "medicine", departmentKey: null, equivalentRate: 94.73, type: "equivalent" },

    // Pharmacy
    { code: "1100", collegeKey: "pharmacy", departmentKey: null, equivalentRate: 92.56, type: "equivalent" },

    // Dentistry
    { code: "1212", collegeKey: "dentistry", departmentKey: null, equivalentRate: 94.42, type: "equivalent" },

    // Engineering & Petroleum
    { code: "0661", collegeKey: "engineering", departmentKey: "industrial_engineering", equivalentRate: 65.38, type: "equivalent" },
    { code: "0631", collegeKey: "engineering", departmentKey: "mechanical_engineering", equivalentRate: 65.72, type: "equivalent" },
    { code: "0611", collegeKey: "engineering", departmentKey: "electrical_engineering", equivalentRate: 71.19, type: "equivalent" },
    { code: "0641", collegeKey: "engineering", departmentKey: "chemical_engineering", equivalentRate: 66.76, type: "equivalent" },
    { code: "0651", collegeKey: "engineering", departmentKey: "petroleum_engineering", equivalentRate: 73.26, type: "equivalent" },
    { code: "0621", collegeKey: "engineering", departmentKey: "civil_engineering", equivalentRate: 71.93, type: "equivalent" },
    { code: "0612", collegeKey: "engineering", departmentKey: "computer_engineering", equivalentRate: 76.56, type: "equivalent" },

    // Architecture
    { code: "1631", collegeKey: "architecture", departmentKey: "interior_design", equivalentRate: 71.02, type: "equivalent" },
    { code: "1621", collegeKey: "architecture", departmentKey: "visual_design", equivalentRate: 65.71, type: "equivalent" },
    { code: "1610", collegeKey: "architecture", departmentKey: "architecture", equivalentRate: 79.29, type: "equivalent" },

    // Arts (Al-Adab)
    { code: "0314", collegeKey: "arts", departmentKey: "arabic_language", equivalentRate: 66.31, type: "equivalent" },
    { code: "0391", collegeKey: "arts", departmentKey: "french_language", equivalentRate: 67.43, type: "equivalent", extraScoreField: "french", minExtraScore: 50 },
    { code: "0331", collegeKey: "arts", departmentKey: "history", equivalentRate: 70.93, type: "equivalent" },
    { code: "0361", collegeKey: "arts", departmentKey: "philosophy", equivalentRate: 66.4, type: "equivalent" },
    { code: "0381", collegeKey: "arts", departmentKey: "media", equivalentRate: 66.6, type: "equivalent" },

    // Arts - English Department
    { code: "0321", collegeKey: "arts_english", departmentKey: "english_language", equivalentRate: 76.88, type: "equivalent" },

    // Sharia & Islamic Studies
    { code: "0933", collegeKey: "sharia", departmentKey: "fiqh_usul", equivalentRate: 74.28, type: "equivalent" },
    { code: "0941", collegeKey: "sharia", departmentKey: "comparative_fiqh", equivalentRate: 66.37, type: "equivalent" },
    { code: "0914", collegeKey: "sharia", departmentKey: "tafsir_hadith", equivalentRate: 66.64, type: "equivalent" },
    { code: "0921", collegeKey: "sharia", departmentKey: "aqeedah_dawah", equivalentRate: 66.43, type: "equivalent" },

    // Social Sciences
    { code: "1371", collegeKey: "social_sciences", departmentKey: "sociology", equivalentRate: 76.12, type: "equivalent" },
    { code: "1372", collegeKey: "social_sciences", departmentKey: "social_work", equivalentRate: 77.57, type: "equivalent" },
    { code: "1351", collegeKey: "social_sciences", departmentKey: "psychology", equivalentRate: 82.52, type: "equivalent" },
    { code: "1361", collegeKey: "social_sciences", departmentKey: "political_science", equivalentRate: 75.33, type: "equivalent" },
    { code: "1346", collegeKey: "social_sciences", departmentKey: "applied_geography", equivalentRate: 70.76, type: "equivalent" },
    { code: "1347", collegeKey: "social_sciences", departmentKey: "geographic_info", equivalentRate: 70.84, type: "equivalent" },

    // Administrative Sciences
    { code: "1022", collegeKey: "admin_sciences", departmentKey: "accounting", equivalentRate: 73.35, type: "equivalent", track: "scientific" },
    { code: "1022", collegeKey: "admin_sciences_literary", departmentKey: "accounting", equivalentRate: 69, type: "equivalent", track: "literary" },
    { code: "1004", collegeKey: "admin_sciences_literary", departmentKey: "management", equivalentRate: 61.76, type: "equivalent", track: "literary" },
    { code: "1004", collegeKey: "admin_sciences", departmentKey: "management", equivalentRate: 61.46, type: "equivalent", track: "scientific" },
    { code: "1006", collegeKey: "admin_sciences", departmentKey: "marketing", equivalentRate: 58.24, type: "equivalent", track: "scientific" },
    { code: "1006", collegeKey: "admin_sciences_literary", departmentKey: "marketing", equivalentRate: 62.34, type: "equivalent", track: "literary" },
    { code: "1007", collegeKey: "admin_sciences", departmentKey: "finance", equivalentRate: 60.58, type: "equivalent", track: "scientific" },
    { code: "1007", collegeKey: "admin_sciences_literary", departmentKey: "finance", equivalentRate: 61.12, type: "equivalent", track: "literary" },
    { code: "1032", collegeKey: "admin_sciences_literary", departmentKey: "economics", equivalentRate: 64.91, type: "equivalent", track: "literary" },
    { code: "1032", collegeKey: "admin_sciences", departmentKey: "economics", equivalentRate: 63.06, type: "equivalent", track: "scientific" },
    { code: "1009", collegeKey: "admin_sciences_literary", departmentKey: "mis", equivalentRate: 61.12, type: "equivalent", track: "literary" },
    { code: "1009", collegeKey: "admin_sciences", departmentKey: "mis", equivalentRate: 56.91, type: "equivalent", track: "scientific" },
    { code: "1023", collegeKey: "admin_sciences", departmentKey: "operations", equivalentRate: 60.85, type: "equivalent", track: "scientific" },
    { code: "1023", collegeKey: "admin_sciences_literary", departmentKey: "operations", equivalentRate: 69.93, type: "equivalent", track: "literary" },
    { code: "1061", collegeKey: "admin_sciences", departmentKey: "public_admin", equivalentRate: 57.79, type: "equivalent", track: "scientific" },
    { code: "1061", collegeKey: "admin_sciences_literary", departmentKey: "public_admin", equivalentRate: 66.61, type: "equivalent", track: "literary" },

    // Education - Literary — برنامج متوسط وثانوي (Middle & Secondary)
    { code: "0851", collegeKey: "education_literary", departmentKey: "arabic_language", equivalentRate: 80.32, type: "equivalent", stage: "middle_secondary" },
    { code: "0859", collegeKey: "education_literary", departmentKey: "islamic_studies", equivalentRate: 82.52, type: "equivalent", stage: "middle_secondary" },
    { code: "0852", collegeKey: "education_literary", departmentKey: "english_language", equivalentRate: 81.85, type: "equivalent", stage: "middle_secondary" },
    { code: "0853", collegeKey: "education_literary", departmentKey: "history", equivalentRate: 84.47, type: "equivalent", stage: "middle_secondary" },
    { code: "0854", collegeKey: "education_literary", departmentKey: "geography", equivalentRate: 84.13, type: "equivalent", stage: "middle_secondary" },
    { code: "0856", collegeKey: "education_literary", departmentKey: "philosophy", equivalentRate: 82.43, type: "equivalent", stage: "middle_secondary" },
    { code: "0855", collegeKey: "education_literary", departmentKey: "psychology", equivalentRate: 82.83, type: "equivalent", stage: "middle_secondary" },
    // Kindergarten + Primary programmes (name already implies stage).
    // PDF marks all of these as female-only (تخصصات للطالبات فقط).
    { code: "0857", collegeKey: "education_literary", departmentKey: "kindergarten", equivalentRate: 85.44, type: "equivalent" },
    { code: "0872", collegeKey: "education_literary", departmentKey: "primary_arabic", equivalentRate: 81.44, type: "equivalent" },
    { code: "0858", collegeKey: "education_literary", departmentKey: "primary_english", equivalentRate: 83.59, type: "equivalent" },
    { code: "0873", collegeKey: "education_literary", departmentKey: "primary_islamic", equivalentRate: 83.14, type: "equivalent" },
    { code: "0879", collegeKey: "education_literary", departmentKey: "primary_social", equivalentRate: 85.23, type: "equivalent" },

    // Education - Scientific
    // Primary programmes (name already implies stage; female-only per PDF)
    { code: "0867", collegeKey: "education_scientific", departmentKey: "primary_science", equivalentRate: 82.59, type: "equivalent" },
    { code: "0865", collegeKey: "education_scientific", departmentKey: "primary_math", equivalentRate: 77.59, type: "equivalent" },
    // Middle & Secondary science programmes
    { code: "0863", collegeKey: "education_scientific", departmentKey: "physics", equivalentRate: 77.87, type: "equivalent", stage: "middle_secondary" },
    { code: "0862", collegeKey: "education_scientific", departmentKey: "chemistry", equivalentRate: 78.08, type: "equivalent", stage: "middle_secondary" },
    { code: "0864", collegeKey: "education_scientific", departmentKey: "biology", equivalentRate: 82.93, type: "equivalent", stage: "middle_secondary" },
    { code: "0866", collegeKey: "education_scientific", departmentKey: "geology", equivalentRate: 82.32, type: "equivalent", stage: "middle_secondary" },
    { code: "0861", collegeKey: "education_scientific", departmentKey: "math", equivalentRate: 75.9, type: "equivalent", stage: "middle_secondary" },
    // These humanities specialties live under Edu-Sci but use the literary formula
    // (HS 80% / Arabic 10% / English 10%; Math excluded).
    { code: "0851", collegeKey: "education_scientific", departmentKey: "arabic_language", equivalentRate: 80.32, type: "equivalent", weightsOverride: collegeWeights.education_literary, stage: "middle_secondary" },
    { code: "0852", collegeKey: "education_scientific", departmentKey: "english_language", equivalentRate: 81.85, type: "equivalent", weightsOverride: collegeWeights.education_literary, stage: "middle_secondary" },
    { code: "0859", collegeKey: "education_scientific", departmentKey: "islamic_studies", equivalentRate: 82.52, type: "equivalent", weightsOverride: collegeWeights.education_literary, stage: "middle_secondary" },
    { code: "0872", collegeKey: "education_scientific", departmentKey: "primary_arabic", equivalentRate: 81.44, type: "equivalent", weightsOverride: collegeWeights.education_literary },
    { code: "0858", collegeKey: "education_scientific", departmentKey: "primary_english", equivalentRate: 83.59, type: "equivalent", weightsOverride: collegeWeights.education_literary },
    { code: "0873", collegeKey: "education_scientific", departmentKey: "primary_islamic", equivalentRate: 83.14, type: "equivalent", weightsOverride: collegeWeights.education_literary },

    // Science (uses high school % directly)
    { code: "0493", collegeKey: "science", departmentKey: "animal_biology", equivalentRate: 85.33, type: "high_school" },
    { code: "0494", collegeKey: "science", departmentKey: "plant_biology", equivalentRate: 82.57, type: "high_school" },
    { code: "0495", collegeKey: "science", departmentKey: "microbiology", equivalentRate: 95.6, type: "high_school" },
    { code: "0496", collegeKey: "science", departmentKey: "biochemistry", equivalentRate: 92.57, type: "high_school" },
    { code: "0497", collegeKey: "science", departmentKey: "molecular_biology", equivalentRate: 93.93, type: "high_school" },
    { code: "0411", collegeKey: "science", departmentKey: "mathematics", equivalentRate: 75.88, type: "high_school" },
    { code: "0421", collegeKey: "science", departmentKey: "chemistry_dept", equivalentRate: 81.8, type: "high_school" },
    { code: "0422", collegeKey: "science", departmentKey: "applied_chemistry", equivalentRate: 81.04, type: "high_school" },
    { code: "0432", collegeKey: "science", departmentKey: "basic_physics", equivalentRate: 82.23, type: "high_school" },
    { code: "0435", collegeKey: "science", departmentKey: "engineering_physics", equivalentRate: 79.24, type: "high_school" },
    { code: "0416", collegeKey: "science", departmentKey: "cybersecurity", equivalentRate: 90.65, type: "high_school" },
    { code: "0414", collegeKey: "science", departmentKey: "financial_math", equivalentRate: 72.65, type: "high_school" },
    { code: "0436", collegeKey: "science", departmentKey: "laser_telecom", equivalentRate: 79.74, type: "high_school" },
    { code: "0461", collegeKey: "science", departmentKey: "geology", equivalentRate: 80.21, type: "high_school" },
    { code: "0464", collegeKey: "science", departmentKey: "marine_science", equivalentRate: 79.09, type: "high_school" },
    { code: "0481", collegeKey: "science", departmentKey: "statistics", equivalentRate: 70, type: "high_school" },
    { code: "0482", collegeKey: "science", departmentKey: "applied_statistics", equivalentRate: 74.27, type: "high_school" },
    { code: "0418", collegeKey: "science", departmentKey: "computer_science", equivalentRate: 83.06, type: "high_school" },

    // Law (uses high school % directly)
    { code: "0200", collegeKey: "law", departmentKey: null, equivalentRate: 85.76, type: "high_school" },

    // Life Sciences
    { code: "1832", collegeKey: "life_sciences", departmentKey: "data_science_ai", equivalentRate: 70.16, type: "equivalent" },
    { code: "1822", collegeKey: "life_sciences", departmentKey: "food_science", equivalentRate: 76.62, type: "equivalent" },
    { code: "1821", collegeKey: "life_sciences", departmentKey: "nutrition", equivalentRate: 82.55, type: "equivalent" },
    { code: "1811", collegeKey: "life_sciences", departmentKey: "communication_disorders", equivalentRate: 84.12, type: "equivalent", additionalConditions: "personal_interview_communication_disorders" },
    { code: "1860", collegeKey: "life_sciences", departmentKey: "environmental_science", equivalentRate: 63.42, type: "equivalent" },
    { code: "1831", collegeKey: "life_sciences", departmentKey: "information_science", equivalentRate: 62.78, type: "equivalent" },

    // Allied Medical Sciences
    { code: "0731", collegeKey: "allied_medical", departmentKey: "diagnostic_radiology", equivalentRate: 89.31, type: "equivalent" },
    { code: "0723", collegeKey: "allied_medical", departmentKey: "nursing", equivalentRate: 88.49, type: "equivalent" },
    { code: "0716", collegeKey: "allied_medical", departmentKey: "occupational_therapy", equivalentRate: 90.6, type: "equivalent" },
    { code: "0713", collegeKey: "allied_medical", departmentKey: "physical_therapy", equivalentRate: 91.31, type: "equivalent" },
    { code: "0712", collegeKey: "allied_medical", departmentKey: "medical_lab", equivalentRate: 88.71, type: "equivalent" },
    { code: "0711", collegeKey: "allied_medical", departmentKey: "health_info", equivalentRate: 86.77, type: "equivalent" },

    // Public Health
    { code: "1900", collegeKey: "public_health", departmentKey: null, equivalentRate: 83.68, type: "equivalent" },
];

// College list with display order
export const colleges: College[] = [
    // ── Scientific ─────────────────────────────────────────────────
    {
        key: "medicine",
        emoji: "🩺",
        track: "scientific",
        weights: collegeWeights.medicine,
        departments: departments.filter((d) => d.collegeKey === "medicine"),
    },
    {
        key: "dentistry",
        emoji: "🦷",
        track: "scientific",
        weights: collegeWeights.dentistry,
        departments: departments.filter((d) => d.collegeKey === "dentistry"),
    },
    {
        key: "pharmacy",
        emoji: "💊",
        track: "scientific",
        weights: collegeWeights.pharmacy,
        departments: departments.filter((d) => d.collegeKey === "pharmacy"),
    },
    {
        key: "public_health",
        emoji: "🥼",
        track: "scientific",
        weights: collegeWeights.public_health,
        departments: departments.filter((d) => d.collegeKey === "public_health"),
    },
    {
        key: "allied_medical",
        emoji: "🏥",
        track: "scientific",
        weights: collegeWeights.allied_medical,
        departments: departments.filter((d) => d.collegeKey === "allied_medical"),
    },
    {
        key: "engineering",
        emoji: "👷",
        track: "scientific",
        weights: collegeWeights.engineering,
        departments: departments.filter((d) => d.collegeKey === "engineering"),
    },
    {
        key: "architecture",
        emoji: "🏛️",
        track: "scientific",
        weights: collegeWeights.architecture,
        departments: departments.filter((d) => d.collegeKey === "architecture"),
    },
    {
        key: "science",
        emoji: "🔬",
        track: "scientific",
        weights: collegeWeights.science,
        departments: departments.filter((d) => d.collegeKey === "science"),
    },
    {
        key: "education_scientific",
        emoji: "🧪",
        track: "scientific",
        weights: collegeWeights.education_scientific,
        departments: departments.filter((d) => d.collegeKey === "education_scientific"),
    },
    {
        key: "life_sciences",
        emoji: "🧬",
        track: "scientific",
        weights: collegeWeights.life_sciences,
        departments: departments.filter((d) => d.collegeKey === "life_sciences"),
    },
    {
        key: "admin_sciences",
        emoji: "📊",
        track: "scientific",
        weights: collegeWeights.admin_sciences,
        departments: departments.filter((d) => d.collegeKey === "admin_sciences"),
    },
    // ── Literary ───────────────────────────────────────────────────
    // ── Both tabs (scientific & literary students can apply) ─────────
    {
        key: "arts",
        emoji: "📖",
        weights: collegeWeights.arts,
        departments: departments.filter((d) => d.collegeKey === "arts"),
    },
    {
        key: "arts_english",
        emoji: "🔠",
        weights: collegeWeights.arts_english,
        departments: departments.filter((d) => d.collegeKey === "arts_english"),
        parentKey: "arts",
    },
    {
        key: "sharia",
        emoji: "☪️",
        weights: collegeWeights.sharia,
        departments: departments.filter((d) => d.collegeKey === "sharia"),
    },
    {
        key: "law",
        emoji: "⚖️",
        weights: collegeWeights.law,
        departments: departments.filter((d) => d.collegeKey === "law"),
    },
    {
        key: "social_sciences",
        emoji: "🧠",
        weights: collegeWeights.social_sciences,
        departments: departments.filter((d) => d.collegeKey === "social_sciences"),
    },
    {
        key: "admin_sciences_literary",
        emoji: "📊",
        weights: collegeWeights.admin_sciences_literary,
        departments: departments.filter((d) => d.collegeKey === "admin_sciences_literary"),
    },
    // ── Literary only ─────────────────────────────────────────────────
    {
        key: "education_literary",
        emoji: "📚",
        track: "literary",
        weights: collegeWeights.education_literary,
        departments: departments.filter((d) => d.collegeKey === "education_literary"),
    },
];

// Helper: get minimum equivalent rate for a college (lowest department rate)
export function getCollegeMinRate(
    collegeKey: string,
    _gender?: Gender,
    _track?: Track | null,
): number {
    const collegeDepts = departments.filter((d) => d.collegeKey === collegeKey);
    if (collegeDepts.length === 0) return 0;
    return Math.min(...collegeDepts.map((d) => d.equivalentRate));
}

// Helper: get required input fields for a college
export function getRequiredFields(weights: CollegeWeights): string[] {
    const fields: string[] = ["high_school"];
    if (weights.englishWeight > 0) fields.push("english");
    if (weights.mathWeight > 0) fields.push("math");
    if (weights.arabicWeight > 0) fields.push("arabic");
    if (weights.chemistryWeight > 0) fields.push("chemistry");
    return fields;
}

// Helper: get unique specialties for a college (de-duplicated by departmentKey).
// Includes departments from any child colleges (those with parentKey === collegeKey)
// so nested departments appear in the parent's specialty list.
export function getCollegeSpecialties(
    collegeKey: string,
    _gender?: Gender,
    _track?: Track | null,
): Department[] {
    const childKeys = colleges.filter((c) => c.parentKey === collegeKey).map((c) => c.key);
    const eligibleKeys = new Set([collegeKey, ...childKeys]);
    return departments.filter(
        (dept, index, self) =>
            eligibleKeys.has(dept.collegeKey) &&
            dept.departmentKey !== null &&
            self.findIndex(
                (d) => d.departmentKey === dept.departmentKey && d.collegeKey === dept.collegeKey,
            ) === index,
    );
}

// Helper: check if a college has selectable specialties (more than 1 unique department)
export function hasSelectableSpecialties(
    collegeKey: string,
    _gender?: Gender,
    _track?: Track | null,
): boolean {
    return getCollegeSpecialties(collegeKey).length > 1;
}

/* ── Kuwait University equivalent-rate calculation (isolated) ────────────
 * Owned by this module so KU's formula can never be touched by edits to
 * any other university (PAAET, AASU, IM, FM).
 */
export function calculateEquivalentRate(weights: CollegeWeights, scores: Scores): number {
    if (weights.admissionType === "high_school") {
        return scores.high_school;
    }
    const rate =
        (scores.high_school * weights.highSchoolWeight) / 100 +
        ((scores.english || 0) * weights.englishWeight) / 100 +
        ((scores.math || 0) * weights.mathWeight) / 100 +
        ((scores.arabic || 0) * weights.arabicWeight) / 100 +
        ((scores.chemistry || 0) * weights.chemistryWeight) / 100;
    return Math.round(rate * 100) / 100;
}

export function meetsMinHighSchool(weights: CollegeWeights, scores: Scores): boolean {
    if (weights.minHighSchool === null) return true;
    return scores.high_school >= weights.minHighSchool;
}

export function meetsMinEnglish(weights: CollegeWeights, scores: Scores): boolean {
    if (weights.minEnglish === null) return true;
    return (scores.english || 0) >= weights.minEnglish;
}

/* ── Per-section public API (consumed via the registry) ─────────────────
 * Everything below owns KU's percentages, track rules, and display logic.
 * No other section's module reads from here.
 */

/** University-level minimum HS by track (Scientific ≥70%, Literary ≥78%). */
export function getTrackMinHighSchool(track: Track | null | undefined): number | null {
    if (!track) return null;
    return track === "scientific" ? 70 : 78;
}

/** KU's per-college minHighSchool (e.g. Medicine 85%, Pharmacy 80%) is a
 *  hard apply-floor — students whose HS percentage is below the column
 *  shown in red on the college tile cannot proceed to the score step. The
 *  track-min (70%/78%) remains the upstream hard gate.
 *
 *  Science & Law are the exception: they compete on last-year's accepted HS
 *  rate, surfaced as a target rather than an apply-floor. Their only hard gate
 *  is the track minimum, so a student at/above 70% can always calculate and
 *  the shortfall is reported as a failure on the results step. */
export function isCollegeMinHardGate(collegeKey?: string): boolean {
    if (collegeKey === "science" || collegeKey === "law") {
        return false;
    }
    return true;
}

/** Gating threshold used in the qualified-colleges filter — KU competes on
 *  the published equivalent rate. */
export function getDepartmentThreshold(dept: Department): number {
    return dept.equivalentRate;
}

/** Displayed "min rate" next to the student's rate. KU has no per-track
 *  override, so it returns the supplied fallback (= equivalentRate). */
export function getDisplayedMinRate(
    _dept: Department | null,
    _track: Track | null | undefined,
    _weights: CollegeWeights,
    fallbackMinRate: number,
): number {
    return fallbackMinRate;
}

/** i18n key for the label preceding the displayed min rate. */
export function getDisplayedMinRateLabelKey(weights: CollegeWeights): string {
    return weights.admissionType === "high_school"
        ? "dream_university_ku_last_year_hs_rate"
        : "dream_university_last_year_rate";
}

/** KU shows no info box on the score step (its gates use the shared track-min
 *  / college-min alerts). */
export function getScoreInfoAlert(
    _college: College,
    _department: Department | null,
    _track: Track | null | undefined,
    _collegeMinHs: number | null,
    _enteredHs: number,
): StepAlert | null {
    return null;
}

/** Results-step failure lines. Science & Law compete on last year's accepted
 *  HS rate, so their second line uses HS-specific wording. */
export function getResultsFailureLines(
    college: College,
    _department: Department | null,
    _track: Track | null | undefined,
    trackMinHs: number | null,
    minRate: number,
): StepAlertLine[] {
    const isSciLaw = college.key === "science" || college.key === "law";
    return [
        {
            key: "dream_university_below_accepted_rate_failure_line1",
            params: { minHs: college.weights.minHighSchool ?? trackMinHs ?? 0 },
        },
        {
            key: isSciLaw
                ? "dream_university_ku_failure_last_year_hs"
                : "dream_university_below_accepted_rate_failure_line2",
            params: { acceptedRate: minRate },
        },
    ];
}

/** Consolidated registry-facing helpers. */
export function getColleges(_gender?: Gender, _track?: Track | null): College[] {
    return colleges;
}

export function getDepartmentsForFilters(_gender?: Gender, _track?: Track | null): Department[] {
    return departments;
}
